export enum InvoiceStatus {
	Approved = 'Approved',
	Created = 'Created',
	Denied = 'Denied',
	Edited = 'Edited',
	EmailSent = 'EmailSent',
	Failed = 'Failed',
	Finalized = 'Finalized',
	Imported = 'Imported',
	InNetSuite = 'InNetSuite',
	PaymentRequestStarted = 'PaymentRequestStarted',
	PaymentRequested = 'PaymentRequested',
	PaymentRequestFailed = 'PaymentRequestFailed',
	Reversed = 'Reversed',
}

type StatusRule = 'editable' | 'approvable' | 'deleteInvoice' | 'deleteProjectPayment' | 'resettable'
	| 'dispatchInvoice' | 'dispatchProjectPayment' | 'paymentRequests' | 'attention' | 'success';

// Every status must be listed. Omitted permissions are denied.
const STATUS_RULES: Record<InvoiceStatus, Partial<Record<StatusRule, true>>> = {
	[InvoiceStatus.Approved]: { resettable: true, dispatchInvoice: true, dispatchProjectPayment: true },
	[InvoiceStatus.Created]: { editable: true, approvable: true, deleteInvoice: true, deleteProjectPayment: true },
	[InvoiceStatus.Denied]: { editable: true, deleteInvoice: true, deleteProjectPayment: true, resettable: true, attention: true },
	[InvoiceStatus.Edited]: { editable: true, approvable: true, deleteInvoice: true, deleteProjectPayment: true },
	[InvoiceStatus.EmailSent]: { paymentRequests: true, success: true },
	[InvoiceStatus.Failed]: { dispatchInvoice: true, dispatchProjectPayment: true, attention: true },
	[InvoiceStatus.Finalized]: { success: true },
	// Imported vendor invoices must be saved before approval or deletion; project payments may be deleted.
	[InvoiceStatus.Imported]: { editable: true, deleteProjectPayment: true },
	[InvoiceStatus.InNetSuite]: { dispatchInvoice: true },
	[InvoiceStatus.PaymentRequestStarted]: { dispatchInvoice: true, dispatchProjectPayment: true, paymentRequests: true, attention: true },
	// Requests succeeded, but pending emails can still be retried.
	[InvoiceStatus.PaymentRequested]: { dispatchInvoice: true, dispatchProjectPayment: true, paymentRequests: true, success: true },
	[InvoiceStatus.PaymentRequestFailed]: { dispatchInvoice: true, dispatchProjectPayment: true, paymentRequests: true, attention: true },
	[InvoiceStatus.Reversed]: { editable: true, approvable: true, deleteInvoice: true, deleteProjectPayment: true },
};

const allows = (status: unknown, rule: StatusRule): boolean =>
	typeof status === 'string' && Object.prototype.hasOwnProperty.call(STATUS_RULES, status) && STATUS_RULES[status as InvoiceStatus][rule] === true;

export const isEditable = (status: unknown) => allows(status, 'editable');
export const isApprovable = (status: unknown) => allows(status, 'approvable');
export const canDeleteInvoice = (status: unknown) => allows(status, 'deleteInvoice');
export const canDeleteProjPayment = (status: unknown) => allows(status, 'deleteProjectPayment');
export const canResetInvoice = (status: unknown) => allows(status, 'resettable');
export const canDispatchInvoice = (status: unknown) => allows(status, 'dispatchInvoice');
export const canDispatchProjPayment = (status: unknown) => allows(status, 'dispatchProjectPayment');
export const hasPaymentRequests = (status: unknown) => allows(status, 'paymentRequests');
export const needsAttention = (status: unknown) => allows(status, 'attention');
export const isSuccessful = (status: unknown) => allows(status, 'success');
